import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { $$toast } from '@doger/ui/components/ui/toast';
import type { AIModel } from '@doger/core/modules/chat';
import { $$api, ErrorMatcher } from '@doger/core/shared/api';

import { useAuth } from '@doger/chat/modules/auth/auth.context';

import { useTranslation } from '../../../../shared/i18n';
import { ChatCacheManager } from '../chat.cache-manager';
import { CHAT_QUERY_KEYS } from '../chat.query-keys';
import { useSendMessageMutation } from './use-send-message-to-chat.mutation';

type CreateNewChatArgs = {
  model: AIModel;
  text: string;
  from: 'text' | 'suggestion';
};

export const useCreateNewChatMutation = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { setIsWhitelistModalOpen, setIsEmailConfirmationModalOpen } =
    useAuth();
  const { t } = useTranslation('app');

  const sendMessageMutation = useSendMessageMutation();

  return useMutation({
    mutationKey: CHAT_QUERY_KEYS.mutations.createNewChat(),
    mutationFn: async (args: CreateNewChatArgs) => {
      const session = await $$api.createSession({
        appName: args.model,
      });

      const manager = new ChatCacheManager(queryClient);

      manager.addNewChat({ session });

      navigate(`/c/${session.id}`);

      sendMessageMutation.mutate({
        sessionId: session.id,
        text: args.text,
        appName: args.model,
        from: args.from,
        type: 'create',
      });
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: CHAT_QUERY_KEYS.queries.sessions(),
      });
    },
    onError: (error) => {
      if (ErrorMatcher.isAppError(error, 'NOT_ON_WHITELIST')) {
        $$toast.error(t('whitelist.toast.pending-application.title'));

        return;
      }

      if (ErrorMatcher.isAppError(error, 'EMAIL_NOT_CONFIRMED')) {
        $$toast.error(t('auth.emailNotConfirmed'), {
          action: {
            label: t('auth.emailConfirmationReminder.actionLabel', {
              ns: 'core',
            }),
            onClick: () => {
              setIsEmailConfirmationModalOpen(true);
            },
          },
        });

        return;
      }

      if (ErrorMatcher.isAppError(error, 'COMPLETED_PROFILE_REQUIRED')) {
        $$toast.error(t('whitelist.toast.not-submitted-application.title'), {
          action: {
            label: t('whitelist.toast.not-submitted-application.action'),
            onClick: () => {
              setIsWhitelistModalOpen(true);
            },
          },
        });

        return;
      }

      $$toast.error(t('errors.chat.failedToCreate'));
    },
  });
};
